import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ArrowLeft } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/leads`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! Your message has been sent.");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewMessage = () => {
    setIsSubmitted(false);
  };

  return (
    <section id="contact" className="py-14 sm:py-18 lg:py-22">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Let's Talk About Your Property Goals
          </h2>
          <p className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto font-medium">
            Get your free property analysis and discover how much more you could be earning
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <Card className="shadow-[0_10px_30px_rgba(15,42,74,0.08)]">
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardContent className="py-16 text-center" data-testid="contact-form-success">
                    <div className="flex justify-center mb-6">
                      <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-primary" />
                      </div>
                    </div>
                    <h3 className="font-display text-2xl font-bold text-foreground mb-3">
                      Thank You!
                    </h3>
                    <p className="text-lg text-foreground/70 mb-2 max-w-md mx-auto">
                      Your message has been received. Our team will reach out to you shortly.
                    </p>
                    <p className="text-sm text-foreground/50 mb-8">
                      You can also call us directly at{" "}
                      <a href="tel:913-795-1155" className="text-primary font-semibold hover:underline">
                        913-795-1155
                      </a>
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleNewMessage}
                      data-testid="contact-form-new-message-button"
                      className="gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Send Another Message
                    </Button>
                  </CardContent>
                </motion.div>
              ) : (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <CardHeader>
                    <CardTitle className="font-display">Contact Us</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form
                      onSubmit={handleSubmit}
                      data-testid="contact-form"
                      className="space-y-6"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="name">Name</Label>
                        <Input
                          id="name"
                          name="name"
                          data-testid="contact-form-name-input"
                          placeholder="Your name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="contact-form-email-input"
                          placeholder="your@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Message</Label>
                        <Textarea
                          id="message"
                          name="message"
                          data-testid="contact-form-message-textarea"
                          placeholder="Tell us about your property management needs..."
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        data-testid="contact-form-submit-button"
                        disabled={isSubmitting}
                        className="w-full text-lg px-8 py-6 h-auto"
                      >
                        {isSubmitting ? "Sending..." : "Send Message"}
                      </Button>
                    </form>
                  </CardContent>
                </motion.div>
              )}
            </AnimatePresence>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
